// ===========================================
// Pricing & Payment React Query Hooks
// ===========================================
// Custom hooks for pricing API operations with React Query

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  pricingMatrixApi,
  bankAccountApi,
  paymentFrequenciesApi,
  pricingConfigApi,
  flexiblePricingMatrixApi,
  BulkPriceUpdate,
  BankAccount,
  PaymentFrequencySettings,
  PricingConfig,
  FlexibleBulkPriceUpdate,
} from '../api/pricing.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const pricingKeys = {
  all: ['pricing'] as const,
  matrix: () => [...pricingKeys.all, 'matrix'] as const,
  // Under matrix(): every price save invalidates matrix(), which refreshes this too.
  matrixRequired: () => [...pricingKeys.all, 'matrix', 'required'] as const,
  config: () => [...pricingKeys.all, 'config'] as const,
  flexibleMatrix: () => [...pricingKeys.all, 'flexibleMatrix'] as const,
  bankAccount: () => [...pricingKeys.all, 'bankAccount'] as const,
  paymentFrequencies: () => [...pricingKeys.all, 'paymentFrequencies'] as const,
};

// ===========================================
// PRICING MATRIX HOOKS
// ===========================================

/**
 * Get the full pricing matrix (program levels x durations)
 */
export function usePricingMatrix() {
  return useQuery({
    queryKey: pricingKeys.matrix(),
    queryFn: pricingMatrixApi.get,
  });
}

/**
 * The cells invoicing will ask for — outlined on the matrix before a run holds a family back
 */
export function useRequiredPricing() {
  return useQuery({
    queryKey: pricingKeys.matrixRequired(),
    queryFn: pricingMatrixApi.getRequired,
    // Class/enrolment edits elsewhere change the answer and do not invalidate this key, so never
    // serve a cached verdict on arrival or return to the tab (QA M1: a stale outline sends the
    // admin to type a duplicate price on the wrong row).
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Bulk update prices in the pricing matrix
 */
export function useBulkUpdatePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prices: BulkPriceUpdate[]) => pricingMatrixApi.bulkUpdate(prices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.matrix() });
    },
  });
}

/**
 * Update a single price entry
 */
export function useUpdatePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { price?: number; isActive?: boolean } }) =>
      pricingMatrixApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.matrix() });
    },
  });
}

/**
 * Delete a price entry
 */
export function useDeletePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pricingMatrixApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.matrix() });
    },
  });
}

// ===========================================
// BANK ACCOUNT HOOKS
// ===========================================

/**
 * Get bank account details
 */
export function useBankAccount() {
  return useQuery({
    queryKey: pricingKeys.bankAccount(),
    queryFn: bankAccountApi.get,
  });
}

/**
 * Update bank account details
 */
export function useUpdateBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BankAccount) => bankAccountApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.bankAccount() });
    },
  });
}

// ===========================================
// PAYMENT FREQUENCIES HOOKS
// ===========================================

/**
 * Get enabled payment frequencies
 */
export function usePaymentFrequencies() {
  return useQuery({
    queryKey: pricingKeys.paymentFrequencies(),
    queryFn: paymentFrequenciesApi.get,
  });
}

/**
 * Update payment frequency settings
 */
export function useUpdatePaymentFrequencies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PaymentFrequencySettings) => paymentFrequenciesApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.paymentFrequencies() });
    },
  });
}

// ===========================================
// FLEXIBLE PRICING HOOKS
// ===========================================

/**
 * Get the school's pricing dimension configuration
 */
export function usePricingConfig() {
  return useQuery({
    queryKey: pricingKeys.config(),
    queryFn: pricingConfigApi.get,
  });
}

/**
 * Update the school's pricing dimension configuration
 */
export function useUpdatePricingConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PricingConfig) => pricingConfigApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.config() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.flexibleMatrix() });
    },
  });
}

/**
 * Get the flexible pricing matrix (dimension rows x durations)
 */
export function useFlexiblePricingMatrix() {
  return useQuery({
    queryKey: pricingKeys.flexibleMatrix(),
    queryFn: flexiblePricingMatrixApi.get,
  });
}

/**
 * Bulk update prices in the flexible pricing matrix
 */
export function useBulkUpdateFlexiblePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prices: FlexibleBulkPriceUpdate[]) => flexiblePricingMatrixApi.bulkUpdate(prices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.flexibleMatrix() });
    },
  });
}
